<script setup lang="ts">
import { computed, onMounted, watch } from 'vue'
import type { ApiClient } from '@xjlv2/api-client'
import type { ModuleMenuItem } from '../../types'
import type { PushTab } from './types'
import { usePushCenterState } from './usePushCenterState'

import PushChannelManager from './PushChannelManager.vue'
import PushTemplateManager from './PushTemplateManager.vue'
import PushWechatManager from './PushWechatManager.vue'
import PushNotificationManager from './PushNotificationManager.vue'
import PushTaskManager from './PushTaskManager.vue'
import PushDeviceManager from './PushDeviceManager.vue'
import PushDeliveryLogs from './PushDeliveryLogs.vue'
import PushUserPreferences from './PushUserPreferences.vue'

const props = defineProps<{
  createApiClient: () => ApiClient
  menu: ModuleMenuItem
  canManageApp: boolean
  canManageTemplate: boolean
  canManageNotification: boolean
  canSendTask: boolean
  canManageDevice: boolean
}>()

const emit = defineEmits<{
  notice: [message: string]
  error: [message: string]
}>()

function tabFromMenu(code?: string): PushTab {
  if (!code) return 'configs'
  if (code.endsWith('.templates')) return 'templates'
  if (code.endsWith('.wechat')) return 'wechat'
  if (code.endsWith('.devices')) return 'devices'
  if (code.endsWith('.notifications')) return 'notifications'
  if (code.endsWith('.tasks')) return 'tasks'
  if (code.endsWith('.logs')) return 'logs'
  if (code.endsWith('.settings')) return 'settings'
  return 'configs'
}

const currentTab = computed<PushTab>(() => tabFromMenu(props.menu?.code))

const pushState = usePushCenterState({
  createApiClient: props.createApiClient,
  onError: (msg) => emit('error', msg),
  onNotice: (msg) => emit('notice', msg)
})

function updateAppCode(code: string) {
  pushState.appCode.value = code
  if (currentTab.value === 'tasks') pushState.taskPageNo.value = 1
  if (currentTab.value === 'devices') pushState.devicePageNo.value = 1
  if (currentTab.value === 'wechat') {
    pushState.wechatTemplatePageNo.value = 1
    pushState.wechatBindingPageNo.value = 1
    pushState.wechatSubscriptionPageNo.value = 1
  }
  void pushState.fetchTab(currentTab.value)
}

function searchTasks(keyword: string, appCode: string) {
  pushState.appCode.value = appCode
  pushState.taskKeyword.value = keyword
  pushState.taskPageNo.value = 1
  void refreshCurrentTab()
}

function changeTaskPage(pageNo: number, pageSize: number) {
  pushState.taskPageNo.value = pageNo
  pushState.taskPageSize.value = pageSize
  void refreshCurrentTab()
}

function searchDevices(keyword: string, appCode: string) {
  pushState.appCode.value = appCode
  pushState.deviceKeyword.value = keyword
  pushState.devicePageNo.value = 1
  void refreshCurrentTab()
}

function changeDevicePage(pageNo: number, pageSize: number) {
  pushState.devicePageNo.value = pageNo
  pushState.devicePageSize.value = pageSize
  void refreshCurrentTab()
}

function searchLogs(taskId: string) {
  const value = taskId.trim()
  if (value && !/^[1-9]\d*$/.test(value)) {
    emit('error', '请输入有效的任务 ID')
    return
  }
  pushState.taskIdFilter.value = value
  pushState.logPageNo.value = 1
  void refreshCurrentTab()
}

function changeLogPage(pageNo: number, pageSize: number) {
  pushState.logPageNo.value = pageNo
  pushState.logPageSize.value = pageSize
  void refreshCurrentTab()
}

function searchNotifications(keyword: string, category: string, appCode: string) {
  pushState.appCode.value = appCode
  pushState.notificationKeyword.value = keyword
  pushState.notificationCategory.value = category
  pushState.notificationPageNo.value = 1
  void refreshCurrentTab()
}

function searchTemplates(keyword: string, enabled: string, appCode: string) {
  pushState.appCode.value = appCode
  pushState.templateKeyword.value = keyword
  pushState.templateEnabled.value = enabled
  pushState.templatePageNo.value = 1
  void refreshCurrentTab()
}

function changeTemplatePage(pageNo: number, pageSize: number) {
  pushState.templatePageNo.value = pageNo
  pushState.templatePageSize.value = pageSize
  void refreshCurrentTab()
}

function changeConfigPage(pageNo: number, pageSize: number) {
  pushState.configPageNo.value = pageNo
  pushState.configPageSize.value = pageSize
  void refreshCurrentTab()
}

function changeSettingPage(pageNo: number, pageSize: number) {
  pushState.settingPageNo.value = pageNo
  pushState.settingPageSize.value = pageSize
  void refreshCurrentTab()
}

function searchWechat(keyword: string) {
  pushState.wechatKeyword.value = keyword
  pushState.wechatTemplatePageNo.value = 1
  pushState.wechatBindingPageNo.value = 1
  pushState.wechatSubscriptionPageNo.value = 1
  void refreshCurrentTab()
}

function changeWechatPage(
  tab: 'templates' | 'bindings' | 'subscriptions',
  pageNo: number,
  pageSize: number
) {
  if (tab === 'templates') {
    pushState.wechatTemplatePageNo.value = pageNo
    pushState.wechatTemplatePageSize.value = pageSize
  } else if (tab === 'bindings') {
    pushState.wechatBindingPageNo.value = pageNo
    pushState.wechatBindingPageSize.value = pageSize
  } else {
    pushState.wechatSubscriptionPageNo.value = pageNo
    pushState.wechatSubscriptionPageSize.value = pageSize
  }
  void refreshCurrentTab()
}

function changeNotificationPage(pageNo: number, pageSize: number) {
  pushState.notificationPageNo.value = pageNo
  pushState.notificationPageSize.value = pageSize
  void refreshCurrentTab()
}

function refreshCurrentTab() {
  return pushState.fetchTab(currentTab.value)
}

watch(currentTab, (newTab) => {
  void pushState.fetchTab(newTab)
})

onMounted(async () => {
  await pushState.fetchApps()
  await pushState.fetchTab(currentTab.value)
})

defineExpose({
  refresh: () => pushState.fetchTab(currentTab.value)
})
</script>

<template>
  <section class="panel span management-list push-center-panel">
    <!-- 1. 消息模板 -->
    <PushTemplateManager
      v-if="currentTab === 'templates'"
      :templates="pushState.templates.value || []"
      :total="pushState.templateTotal.value"
      :page="pushState.templatePageNo.value"
      :page-size="pushState.templatePageSize.value"
      :keyword="pushState.templateKeyword.value"
      :enabled-filter="pushState.templateEnabled.value"
      :query-loading="pushState.loading.value"
      :apps="pushState.apps.value || []"
      :app-code="pushState.appCode.value"
      :can-manage="canManageTemplate"
      :create-api-client="createApiClient"
      @update:app-code="updateAppCode"
      @search="searchTemplates"
      @page-change="changeTemplatePage"
      @refresh="refreshCurrentTab"
      @notice="(msg) => emit('notice', msg)"
      @error="(msg) => emit('error', msg)"
    />

    <!-- 2. 应用与通道 -->
    <PushChannelManager
      v-else-if="currentTab === 'configs'"
      :apps="pushState.apps.value || []"
      :configs="pushState.configs.value || []"
      :total="pushState.configTotal.value"
      :page-no="pushState.configPageNo.value"
      :page-size="pushState.configPageSize.value"
      :app-code="pushState.appCode.value"
      :selected-app-name="pushState.selectedAppName.value"
      :can-manage="canManageApp"
      :create-api-client="createApiClient"
      :test-channel="pushState.testChannel"
      :toggle-channel="pushState.toggleChannel"
      @update:app-code="updateAppCode"
      @refresh="refreshCurrentTab"
      @page-change="changeConfigPage"
      @notice="(msg) => emit('notice', msg)"
      @error="(msg) => emit('error', msg)"
    />

    <!-- 3. 微信触达 -->
    <PushWechatManager
      v-else-if="currentTab === 'wechat'"
      :templates="pushState.wechatChannelTemplates.value || []"
      :template-total="pushState.wechatTemplateTotal.value"
      :template-page="pushState.wechatTemplatePageNo.value"
      :template-page-size="pushState.wechatTemplatePageSize.value"
      :bindings="pushState.wechatBindings.value || []"
      :binding-total="pushState.wechatBindingTotal.value"
      :binding-page="pushState.wechatBindingPageNo.value"
      :binding-page-size="pushState.wechatBindingPageSize.value"
      :subscriptions="pushState.wechatSubscriptions.value || []"
      :subscription-total="pushState.wechatSubscriptionTotal.value"
      :subscription-page="pushState.wechatSubscriptionPageNo.value"
      :subscription-page-size="pushState.wechatSubscriptionPageSize.value"
      :keyword="pushState.wechatKeyword.value"
      :apps="pushState.apps.value || []"
      :app-code="pushState.appCode.value"
      :query-loading="pushState.loading.value"
      :can-manage="canManageTemplate"
      :create-api-client="createApiClient"
      @update:app-code="updateAppCode"
      @search="searchWechat"
      @page-change="changeWechatPage"
      @refresh="refreshCurrentTab"
      @notice="(msg) => emit('notice', msg)"
      @error="(msg) => emit('error', msg)"
    />

    <!-- 4. 通知记录与发布 -->
    <PushNotificationManager
      v-else-if="currentTab === 'notifications'"
      :notifications="pushState.notifications.value || []"
      :total="pushState.notificationTotal.value"
      :page="pushState.notificationPageNo.value"
      :page-size="pushState.notificationPageSize.value"
      :keyword="pushState.notificationKeyword.value"
      :category="pushState.notificationCategory.value"
      :query-loading="pushState.loading.value"
      :notification-stats="pushState.notificationStats.value"
      :apps="pushState.apps.value || []"
      :app-code="pushState.appCode.value"
      :can-manage="canManageNotification"
      :create-api-client="createApiClient"
      :revoke-notification="pushState.revokeNotification"
      :load-notification-detail="pushState.loadNotificationDetail"
      @update:app-code="updateAppCode"
      @search="searchNotifications"
      @page-change="changeNotificationPage"
      @refresh="refreshCurrentTab"
      @notice="(msg) => emit('notice', msg)"
      @error="(msg) => emit('error', msg)"
    />

    <!-- 5. 推送任务 -->
    <PushTaskManager
      v-else-if="currentTab === 'tasks'"
      :tasks="pushState.tasks.value || []"
      :keyword="pushState.taskKeyword.value"
      :total="pushState.taskTotal.value"
      :page="pushState.taskPageNo.value"
      :page-size="pushState.taskPageSize.value"
      :query-loading="pushState.loading.value"
      :templates="pushState.templates.value || []"
      :apps="pushState.apps.value || []"
      :app-code="pushState.appCode.value"
      :can-manage="canSendTask"
      :create-api-client="createApiClient"
      :cancel-task="pushState.cancelTask"
      :retry-task="pushState.retryTask"
      :load-task-detail="pushState.loadTaskDetail"
      @update:app-code="updateAppCode"
      @search="searchTasks"
      @page-change="changeTaskPage"
      @refresh="refreshCurrentTab"
      @notice="(msg) => emit('notice', msg)"
      @error="(msg) => emit('error', msg)"
    />

    <!-- 6. 设备列表 -->
    <PushDeviceManager
      v-else-if="currentTab === 'devices'"
      :devices="pushState.devices.value || []"
      :total="pushState.deviceTotal.value"
      :page="pushState.devicePageNo.value"
      :page-size="pushState.devicePageSize.value"
      :query-loading="pushState.loading.value"
      :apps="pushState.apps.value || []"
      :app-code="pushState.appCode.value"
      :can-manage="canManageDevice"
      :disable-device="pushState.disableDevice"
      @update:app-code="updateAppCode"
      @page-change="changeDevicePage"
      @refresh="refreshCurrentTab"
      @error="(msg) => emit('error', msg)"
      @search="searchDevices"
    />

    <!-- 7. 投递明细 -->
    <PushDeliveryLogs
      v-else-if="currentTab === 'logs'"
      :logs="pushState.logs.value || []"
      :task-id="pushState.taskIdFilter.value"
      :total="pushState.logTotal.value"
      :page="pushState.logPageNo.value"
      :page-size="pushState.logPageSize.value"
      :query-loading="pushState.loading.value"
      @search="searchLogs"
      @page-change="changeLogPage"
    />

    <!-- 8. 用户偏好 -->
    <PushUserPreferences
      v-else
      :settings="pushState.settings.value || []"
      :apps="pushState.apps.value || []"
      :app-code="pushState.appCode.value"
      :total="pushState.settingTotal.value"
      :page-no="pushState.settingPageNo.value"
      :page-size="pushState.settingPageSize.value"
      @update:app-code="updateAppCode"
      @search="
        (userId) => {
          pushState.userIdFilter.value = userId
          void refreshCurrentTab()
        }
      "
      @page-change="changeSettingPage"
    />
  </section>
</template>

<style scoped>
.push-center-panel {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
}
</style>
